import { useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import Hamburger from 'src/icons/Hamburger'
import useSWR from 'swr'

import { ThemesCollectionsDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey, useSlug } from '@/lib/contentful/utils'

import { Link } from '@/components/CustomLink'
import { FilterMenu, FilterOptions } from '@/components/Marketplace'

import { ThemesCollectionButton } from '../ThemesCollectionButton'
// import { ThemesIndustryLink } from '../ThemesIndustryLink'
import { ThemesPriceFilter } from '../ThemesPriceFilter'

type Props = {
	className?: string
}

export const ThemesFilter = ({ className }: Props) => {
	const slug = useSlug()

	const [showFilter, setShowFilter] = useState(false)
	const categoryFilterRef = useRef<HTMLDivElement | null>(null)

	const toggleFilter = () => {
		setShowFilter(!showFilter)
	}

	const handleClickAway = () => {
		setShowFilter(false)
	}

	useClickAway(categoryFilterRef, handleClickAway)

	const { data: collections, isLoading: isCollectionsLoading } = useSWR(
		serializeCacheKey('themesCollections', { ThemesFilter }),
		() =>
			client().request(ThemesCollectionsDocument, {
				where: {
					type: 'collection'
				}
			})
	)

	const { data: industries, isLoading: isIndustriesLoading } = useSWR(
		serializeCacheKey('themesIndustries', { ThemesFilter }),
		() =>
			client().request(ThemesCollectionsDocument, {
				where: {
					type: 'industry'
				}
			})
	)

	const categoryName = useMemo(() => {
		return industries?.themePropertyCollection?.items.find((industry) => industry?.slug === slug)
			?.name
	}, [industries, slug])

	return (
		!isIndustriesLoading &&
		!isCollectionsLoading && (
			<div className={clsx(className, `${showFilter ? 'fixed inset-0 top-0 z-30' : ''}`)}>
				<FilterMenu title={categoryName ?? 'All'} showMenu={showFilter} showRef={categoryFilterRef}>
					{/* Collection Choices */}
					<FilterOptions title="Collections">
						{collections?.themePropertyCollection?.items
							.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
							.map((collection) => (
								<ThemesCollectionButton key={collection?.slug} collection={collection} />
							))}
					</FilterOptions>

					{/* Industry Choices */}
					<FilterOptions title="Industry">
						<div className="flex flex-col">
							{industries?.themePropertyCollection?.items
								.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
								.map((industry) => {
									if (!industry) return null

									const count = industry?.linkedFrom?.marketplaceThemeCollection?.total

									if (count === 0 && industry.name !== 'All') return null

									return (
										<Link
											key={industry.sys.id}
											href={
												industry.slug === 'theme-store'
													? `/${industry.slug}`
													: `/theme-store/${industry.slug}`
											}
											className={`text-gray-500 my-2 cursor-pointer ${
												slug === industry.slug || (!slug && industry.slug === 'theme-store')
													? 'underline underline-offset-8 decoration-bc-blue'
													: ''
											}`}
										>
											{industry.slug === 'theme-store' ? (
												industry.name
											) : (
												<>
													{industry?.name}
													<span className="text-gray-500"> ({count})</span>
												</>
											)}
										</Link>
									)
								})}
						</div>
					</FilterOptions>

					{/* Price */}
					<FilterOptions title="Price">
						<ThemesPriceFilter />
					</FilterOptions>
				</FilterMenu>

				{/* Filter Button */}
				<div className="lg:hidden flex w-full justify-center">
					<button
						className="px-6 py-3 bg-bc-black text-sm text-white font-medium rounded-md flex items-center"
						onClick={toggleFilter}
					>
						<Hamburger />
						<span className="ml-2">Filters</span>
					</button>
				</div>
			</div>
		)
	)
}
